import { Platform } from '@ionic/angular';
import { File } from '@ionic-native/file/ngx';
import { FileOpener } from '@ionic-native/file-opener/ngx';
import { IHttpRequestError } from 'src/app/httpconfig.interceptor';
import { ILang } from 'src/app/model/lang.interface';
import { Lead } from 'src/app/model/lead.model';
import { AppService } from './app.service';
import { LeadClientRepositoryBUT } from './lead.clients.repository.but';

// pause() or removing the element before playback starts rejects play() with AbortError: expected, not a failure
export function playIgnoringAbort(media: HTMLMediaElement): void {
    media.play().catch(e => {
        if (!e || e.name !== 'AbortError') {
            throw e;
        }
    });
}

// <audio id="player-<leadId>"> is rendered only once the recording blob is downloaded
export function playLeadRecording(lead: Lead, repository: LeadClientRepositoryBUT, isActive: () => boolean): void {
    const player = document.getElementById('player-' + lead.id) as HTMLAudioElement;
    if (player) {
        playIgnoringAbort(player);
        return;
    }

    repository.getCallFile(lead.callRecordingUrl).then(fileUrl => {
        lead.downloadedCallFileUrl = fileUrl;
        setTimeout(() => {
            const loaded = document.getElementById('player-' + lead.id) as HTMLAudioElement;
            if (loaded && isActive()) {
                // outside the click gesture the webview may refuse to autoplay; the native controls stay usable
                loaded.play().catch(() => { });
            }
        });
    });
}

// The recording link answers only to the bearer token, which neither Android's DownloadManager nor a browser navigation
// sends: they got its JSON "401" instead of the file (#380). So the file comes from the player's authorised request, and
// a phone opens it from the app's own folder, which needs no storage permission on any Android version.
export function downloadLeadRecording(
    lead: Lead,
    repository: LeadClientRepositoryBUT,
    platform: Platform,
    file: File,
    fileOpener: FileOpener,
    appService: AppService,
    lang: ILang
): void {
    const name = 'recording' + lead.phone + '.mp3';
    repository.getCallBlob(lead.callRecordingUrl).then(blob => {
        if (!platform.is('cordova')) {
            const anchor = document.createElement('a');
            anchor.href = window.URL.createObjectURL(blob);
            anchor.download = name;
            anchor.click();
            return;
        }
        return file.writeFile(file.dataDirectory, name, blob, { replace: true })
            .then(entry => fileOpener.open(entry.nativeURL, 'audio/mpeg'));
    }).catch(reason => {
        // A server answer (no recording, rate limit) or a failed request (lost connection, error status) is not an app
        // failure. The interceptor hands a failed request on as an Error with the HTTP status, never HttpErrorResponse;
        // file-opener's own failures are plain objects with a numeric status, so they still get reported.
        const requestFailed = reason instanceof Error && typeof (reason as IHttpRequestError).status === 'number';
        if (!requestFailed && !(reason && reason.responseCode)) {
            appService.logToSentry('Call recording download failed', { reason: (reason && reason.message) || JSON.stringify(reason) });
        }
        appService.tooltipPopupActive = true;
        appService.titleOfTooltipPopup = lang.words['error-short'];
        appService.textOfTooltipPopup = lang.words['recording-download-failed'];
    });
}
